import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  fetchUserProfile,
  updateUserProfile,
} from "../redux/actions/profileAction";
import { toast } from "react-toastify";
import * as other from "../components/other"; // Assuming Modal component exists here
import Address from "./Address";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

export default function Profile() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    loading,
    error,
    data: profile,
    sessionExpired,
  } = useSelector((state) => state.userProfile);

  const userType = useSelector((state) => state.auth.user?.user?.user_type);

  const [form, setForm] = React.useState({
    fullName: "",
    email: "",
    mobileNo: "",
    gender: "",
    vendor_gst: "",
    vendor_shop_name: "",
    address: "",
    pinCode: "",
  });

  const [modalOpen, setModalOpen] = React.useState(false);

  React.useEffect(() => {
    dispatch(fetchUserProfile());
  }, [dispatch]);

  React.useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  React.useEffect(() => {
    if (profile) {
      
      setForm(profile);
    }
  }, [profile]);

  const handleModalSave = (updatedProfile) => {
    const cleanedProfile = Object.fromEntries(
      Object.entries(updatedProfile)
        .filter(([_, v]) => v != null) // drop only `null`/`undefined`
        .map(([k, v]) => [k, v === "" ? null : v]) // turn `''` into `null`
    );

    dispatch(updateUserProfile(cleanedProfile)).then((res) => {
      if (res?.type?.includes("SUCCESS")) {
        // 1️⃣ refetch fresh data
        // dispatch(fetchUserProfile());
        setModalOpen(false);
      } else {
        toast.error("Failed to update profile");
      }
    });
  };

  const Field = ({ label, value }) => (
    <div>
      <label className="block text-sm text-neutral-700 font-medium">
        {label}
      </label>
      <input
        type="text"
        value={value}
        disabled
        className="w-full mt-1 p-2 text-zinc-500 bg-orange-100 rounded"
      />
    </div>
  );

  if (loading || !profile) {
    return (
      <div className="d-flex justify-content-center align-items-center min-h-screen">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading Profile...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-start justify-center p-6">
      <div className="bg-white min-h-[30vh] rounded-2xl shadow-md w-full max-w-[75rem] p-10 flex flex-col items-center">
        {/* Header */}
        <div className="w-full flex flex-col sm:flex-row justify-between sm:items-center mb-10 max-w-6xl">
          <span className="text-3xl text-center sm:text-left mt-5 font-semibold">
            My Profile
          </span>
          <div className="mt-10 flex gap-4 self-stretch sm:justify-end">
            <div className="flex gap-1 flex-col sm:flex-row w-full">
              <button
                onClick={() => setModalOpen(true)}
                className="btn theme-btn px-4"
              >
                Update Profile
              </button>
              <Link to="/address" className="btn theme-outline-btn px-4 w-auto">
                Address
              </Link>
            </div>
          </div>
        </div>

        {/* Session Expired */}
        {sessionExpired && (
          <div className="mb-4 p-4 bg-red-100 border-red-400 text-red-700 rounded">
            Your session has expired. Please log in again.
          </div>
        )}

        {/* Display */}
        <div className="max-w-5xl w-full p-6">
          <form className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <Field label="Name" value={form.fullName} />
            <Field label="Mobile Number" value={form.mobileNo} />
            <Field label="Email" value={form.email} />
            <Field label="Gender" value={form.gender} />
            {userType === "vendor" && (
              <>
                <Field label="Shop Name" value={form.vendor_shop_name} />
                <Field label="GST" value={form.vendor_gst} />
                {/* <Field label="Address" value={form.address} /> */}
                <Field label="Pin Code" value={form.pinCode} />
              </>
            )}

            {/* <Field label="Pin Code"     value={profile.pinCode} /> */}
            {/* <div className="md:col-span-2">
              <label className="block text-sm font-medium text-neutral-700">Address</label>
              <textarea
                rows={3}
                value={profile.address}
                disabled
                className="w-full mt-1 p-2 text-zinc-500 bg-orange-100 rounded resize-none"
              />
            </div> */}
          </form>
        </div>

        {/* Modal */}
        {modalOpen && (
          <other.Modal
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            title="Update Profile"
          >
            <ModalForm
              form={form}
              setForm={setForm}
              onSave={handleModalSave}
              userType={userType}
            />
          </other.Modal>
        )}
        <Address />
      </div>
    </div>
  );
}

// Local Form Component inside Modal
function ModalForm({ form, setForm, onSave, userType }) {
  const [fieldErrors, setFieldErrors] = useState({});

  // Dynamic validation schema based on userType - No required fields
  const validationSchema = Yup.object().shape({
    fullName: Yup.string()
      .notRequired()
      .nullable()
      .test(
        "len",
        "Name must be at least 2 characters",
        (val) => !val || val.length >= 2
      )
      .matches(/^[a-zA-Z\s]*$/, "Name should only contain letters and spaces")
      .nullable(),

    email: Yup.string()
      .notRequired()
      .nullable()
      .email("Please enter a valid email address"),

    mobileNo: Yup.string()
      .notRequired()
      .nullable()
      .matches(/^[0-9]{10}$/, "Please enter a valid 10-digit mobile number"),

    gender: Yup.string().notRequired().nullable(),

    ...(userType === "vendor" && {
      vendor_shop_name: Yup.string()
        .notRequired()
        .nullable()
        .test(
          "len",
          "Shop name must be at least 2 characters",
          (val) => !val || val.length >= 2
        ),

      vendor_gst: Yup.string()
        .notRequired()
        .nullable()
        .matches(
          /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
          "Please enter a valid GST number"
        ),

      pinCode: Yup.string()
        .notRequired()
        .nullable()
        .matches(/^[0-9]{6}$/, "Please enter a valid 6-digit pin code"),
    }),
  });

  // Dynamic validation function for real-time validation
  const validateField = async (fieldName, value) => {
    // Only validate if field has a value (not empty)
    if (!value || value.trim() === "") {
      setFieldErrors((prev) => ({ ...prev, [fieldName]: "" }));
      return true;
    }

    try {
      await validationSchema.validateAt(fieldName, { [fieldName]: value });
      setFieldErrors((prev) => ({ ...prev, [fieldName]: "" }));
      return true;
    } catch (error) {
      setFieldErrors((prev) => ({ ...prev, [fieldName]: error.message }));
      return false;
    }
  };

  const handleSubmit = (values, { setSubmitting }) => {
    // Update the parent form state
    
    setForm(values);
    // Call the original onSave function
    onSave(values);
    setSubmitting(false);
  };

  return (
    <Formik
      initialValues={form}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize={true}
    >
      {({ values, errors, touched, isSubmitting, setFieldValue }) => (
        <Form>
          <div className="grid grid-cols-1 gap-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-neutral-700">
                Name
              </label>
              <Field
                type="text"
                name="fullName"
                onChange={(e) => {
                  setFieldValue("fullName", e.target.value);
                  validateField("fullName", e.target.value);
                }}
                className={`w-full mt-1 p-2 border rounded ${
                  (errors.fullName && touched.fullName) || fieldErrors.fullName
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
              />
              <div className="text-red-500 text-sm mt-1">
                {fieldErrors.fullName ||
                  (errors.fullName && touched.fullName && errors.fullName)}
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-neutral-700">
                Email
              </label>
              <Field
                type="email"
                name="email"
                onChange={(e) => {
                  setFieldValue("email", e.target.value);
                  validateField("email", e.target.value);
                }}
                className={`w-full mt-1 p-2 border rounded ${
                  (errors.email && touched.email) || fieldErrors.email
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
              />
              <div className="text-red-500 text-sm mt-1">
                {fieldErrors.email ||
                  (errors.email && touched.email && errors.email)}
              </div>
            </div>

            {/* Mobile */}
            <div>
              <label className="block text-sm font-medium text-neutral-700">
                Mobile
              </label>
              <Field
                type="tel"
                name="mobileNo"
                value={form.mobileNo}
                disabled
                className="w-full mt-1 p-2 bg-gray-100 text-gray-500 cursor-not-allowed border rounded"
              />
              <ErrorMessage
                name="mobileNo"
                component="div"
                className="text-red-500 text-sm mt-1"
              />
            </div>

            {/* Gender as select */}
            <div>
              <label className="block text-sm font-medium text-neutral-700">
                Gender
              </label>
              <Field
                as="select"
                name="gender"
                className={`w-full mt-1 p-2 border rounded ${
                  errors.gender && touched.gender
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </Field>
              <ErrorMessage
                name="gender"
                component="div"
                className="text-red-500 text-sm mt-1"
              />
            </div>

            {userType === "vendor" && (
              <>
                {/* Shop Name */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700">
                    Shop Name
                  </label>
                  <Field
                    type="text"
                    name="vendor_shop_name"
                    className={`w-full mt-1 p-2 border rounded ${
                      errors.vendor_shop_name && touched.vendor_shop_name
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                  />
                  <ErrorMessage
                    name="vendor_shop_name"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>

                {/* GST */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700">
                    GST
                  </label>
                  <Field
                    type="text"
                    name="vendor_gst"
                    onChange={(e) => {
                      setFieldValue("vendor_gst", e.target.value);
                      validateField("vendor_gst", e.target.value);
                    }}
                    className={`w-full mt-1 p-2 border rounded ${
                      (errors.vendor_gst && touched.vendor_gst) ||
                      fieldErrors.vendor_gst
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                  />
                  <div className="text-red-500 text-sm mt-1">
                    {fieldErrors.vendor_gst ||
                      (errors.vendor_gst &&
                        touched.vendor_gst &&
                        errors.vendor_gst)}
                  </div>
                </div>

                {/* Pin Code */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700">
                    Pin Code
                  </label>
                  <Field
                    type="text"
                    name="pinCode"
                    onChange={(e) => {
                      setFieldValue("pinCode", e.target.value);
                      validateField("pinCode", e.target.value);
                    }}
                    className={`w-full mt-1 p-2 border rounded ${
                      (errors.pinCode && touched.pinCode) || fieldErrors.pinCode
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                  />
                  <div className="text-red-500 text-sm mt-1">
                    {fieldErrors.pinCode ||
                      (errors.pinCode && touched.pinCode && errors.pinCode)}
                  </div>
                </div>
              </>
            )}

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
}
